<script setup lang="ts">
/**
 * The full price ladder for /pricing in the template's pricing-card style:
 * raised white cards on the grey band, a hairline under the price, a "Key
 * features" list with green check marks and one full-width button.
 *
 * The template switches monthly/yearly with a lilac toggle. We do not sell a
 * monthly plan, so the same control switches between the one-time payments
 * (free, single download, Job Search Pack) and the yearly subscription (free,
 * yearly). The free card is in both views so the grid never looks empty.
 */
const { t } = useI18n()
const { isAuthenticated } = useAuthUser()
const {
  getPlan,
  price,
  exportCreditPrice,
  jobPackPrice,
  yearlyPrice,
  yearlyPerMonth,
} = usePremiumPlans()

type Billing = 'one-time' | 'yearly'

interface PricingCard {
  id: string
  /** The template's illustrated pricing icon. */
  icon: string
  name: string
  price: string
  period: string
  note?: string
  features: string[]
  /** Renders the stacked Job Search Pack offer instead of a plain feature list. */
  stack?: boolean
  cta: string
  to: string
  highlight: boolean
  billing: Billing[]
}

const billing = ref<Billing>('one-time')
const isYearly = computed(() => billing.value === 'yearly')

// The pack and the yearly plan go to the premium page for members and to
// sign-up for visitors: their checkout needs an account to attach them to.
const paidTarget = computed(() => (isAuthenticated.value ? '/admin/premium' : '/registration'))

const cards = computed<PricingCard[]>(() => [
  {
    id: 'free',
    icon: '/sofax/pricing-icon-1.svg',
    name: t('homepage.pricingTeaser.free.name'),
    price: price(0),
    period: t('premium.forever'),
    features: [
      t('homepage.pricingTeaser.free.f1'),
      t('homepage.pricingTeaser.free.f2'),
      t('homepage.pricingTeaser.free.f3'),
    ],
    cta: t('homepage.pricingTeaser.free.cta'),
    to: '/free-cv-builder',
    highlight: false,
    billing: ['one-time', 'yearly'],
  },
  {
    id: 'single',
    icon: '/sofax/pricing-icon-3.svg',
    name: t('homepage.pricingTeaser.single.name'),
    price: exportCreditPrice,
    period: t('premium.oneTimePayment'),
    features: [
      t('homepage.pricingTeaser.single.f1'),
      t('homepage.pricingTeaser.single.f2'),
      t('homepage.pricingTeaser.single.f3'),
    ],
    cta: t('homepage.pricingTeaser.single.cta'),
    // Bought without an account, from the CV it cleans: visitors build one first.
    to: isAuthenticated.value ? '/admin/premium' : '/free-cv-builder',
    highlight: false,
    billing: ['one-time'],
  },
  {
    id: 'pack',
    icon: '/sofax/pricing-icon-2.svg',
    name: t('premium.jobPack.title'),
    price: jobPackPrice,
    period: t('premium.pricingPage.pack.period'),
    note: t('premium.noAutoRenew'),
    features: [],
    stack: true,
    cta: t('homepage.pricingTeaser.pack.cta'),
    to: paidTarget.value,
    highlight: true,
    billing: ['one-time'],
  },
  {
    id: 'yearly',
    icon: '/sofax/pricing-icon-2.svg',
    name: t('premium.pricingPage.yearly.name'),
    price: yearlyPrice,
    period: t('premium.year'),
    note: t('premium.perMonth', { price: yearlyPerMonth }),
    features: [
      t('premium.pricingPage.yearly.f1'),
      t('premium.pricingPage.yearly.f2'),
      t('premium.pricingPage.yearly.f3'),
    ],
    cta: t('premium.pricingPage.yearly.cta'),
    to: paidTarget.value,
    highlight: false,
    billing: ['yearly'],
  },
])

const visible = computed(() => cards.value.filter(card => card.billing.includes(billing.value)))

/**
 * The products paid for one job at a time. Each is bought in its own flow,
 * on what that flow parked on the server, so the card links there instead of
 * opening a checkout. A product whose variant is not configured is left out,
 * like everywhere else. The pack's monthly continuation is not here: it is
 * sold only to someone whose pack is ending (PACK_CONTINUATION).
 */
const perJob = computed(() =>
  ([
    { id: 'tailored-pdf', key: 'tailored', to: '/tailor-resume-to-job' },
    { id: 'interview-prep', key: 'interview', to: '/tailor-resume-to-job' },
    { id: 'application-kit', key: 'kit', to: '/tailor-resume-to-job' },
    { id: 'civil-service', key: 'civilService', to: '/civil-service-application' },
    { id: 'nhs-statement', key: 'nhs', to: '/nhs-supporting-statement' },
  ] as const).flatMap((item) => {
    const plan = getPlan(item.id)
    return plan ? [{ ...item, price: price(plan) }] : []
  }),
)
</script>

<template>
  <section id="plans" class="sofax surface-alt">
    <div class="section-space">
      <div class="shell">
        <div class="mx-auto mb-10 max-w-3xl text-center md:mb-[3.75rem]">
          <h2 class="mb-5">{{ t('premium.pricingPage.title') }}</h2>
          <p class="text-lg md:text-xl">{{ t('premium.pricingPage.subtitle') }}</p>
        </div>

        <!-- Billing toggle: lilac track, dark dot, as in the template -->
        <div class="mb-[3.125rem] flex flex-row items-center justify-center gap-6">
          <button
            type="button"
            class="font-title text-base font-bold transition-colors"
            :class="isYearly ? 'text-muted dark:text-gray-400' : 'text-ink dark:text-white'"
            @click="billing = 'one-time'"
          >
            {{ t('premium.pricingPage.toggle.oneTime') }}
          </button>

          <button
            type="button"
            role="switch"
            :aria-checked="isYearly"
            :aria-label="t('premium.pricingPage.toggle.label')"
            class="relative inline-block h-[2.1875rem] w-[4.375rem] shrink-0 rounded-full bg-lilac focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
            @click="billing = isYearly ? 'one-time' : 'yearly'"
          >
            <span
              class="absolute left-[5px] top-1/2 block size-[1.5625rem] -translate-y-1/2 rounded-full bg-black transition-transform duration-300"
              :class="isYearly ? 'translate-x-[2.0625rem]' : 'translate-x-0'"
            />
          </button>

          <button
            type="button"
            class="font-title text-base font-bold transition-colors"
            :class="isYearly ? 'text-ink dark:text-white' : 'text-muted dark:text-gray-400'"
            @click="billing = 'yearly'"
          >
            {{ t('premium.pricingPage.toggle.yearly') }}
          </button>
        </div>

        <ul
          class="mx-auto grid grid-cols-1 gap-x-6 gap-y-10"
          :class="visible.length === 2 ? 'max-w-4xl md:grid-cols-2' : 'md:grid-cols-2 xl:grid-cols-3'"
        >
          <li
            v-for="card in visible"
            :key="card.id"
            class="relative flex flex-col rounded-[0.625rem] border bg-white p-8 shadow-lift transition-all duration-300 md:p-10 dark:bg-gray-800"
            :class="card.highlight ? 'border-ink dark:border-white' : 'border-transparent'"
          >
            <span v-if="card.highlight" class="tag absolute right-8 top-8 md:right-10 md:top-10">
              {{ t('premium.recommended') }}
            </span>

            <!-- Template's pricing icon; dark line art, so the tile stays light -->
            <span class="mb-5 inline-flex size-16 items-center justify-center rounded-[0.625rem] bg-surface">
              <img :src="card.icon" alt="" aria-hidden="true" width="80" height="80" class="size-10" loading="lazy">
            </span>

            <span class="display-heading display-heading-4 mb-4 block">{{ card.name }}</span>

            <div class="display-heading display-heading-2">
              {{ card.price }}
              <span class="pl-2 !text-lg font-normal tracking-normal !text-body dark:!text-gray-300">
                /{{ card.period }}
              </span>
            </div>
            <span v-if="card.note" class="mt-2 block !text-sm !text-muted dark:!text-gray-400">
              {{ card.note }}
            </span>

            <div class="my-6 h-px w-full bg-line dark:bg-gray-700" />

            <!-- The pack's stack opens with its market comparison, not a list heading -->
            <span v-if="!card.stack" class="mb-5 block font-title !text-lg font-semibold !text-ink dark:!text-white">
              {{ t('premium.keyFeatures') }}
            </span>

            <PremiumJobPackStack v-if="card.stack" class="mb-10" />
            <ul v-else class="mb-10 grid grid-cols-1 gap-y-4 font-semibold text-ink dark:text-white">
              <li v-for="feature in card.features" :key="feature" class="flex gap-2">
                <i class="bi bi-check-circle-fill text-xl leading-none text-accent" />
                <span class="!text-base">{{ feature }}</span>
              </li>
            </ul>

            <div class="mt-auto">
              <SofaxBtn :to="card.to" :label="card.cta" :variant="card.highlight ? 'accent' : 'outline'" block />
            </div>
          </li>
        </ul>

        <!-- The plan hidden by the toggle is one click away -->
        <p class="mt-10 text-center text-base">
          <template v-if="isYearly">
            {{ t('premium.jobPack.description') }}
            <button type="button" class="link" @click="billing = 'one-time'">
              {{ t('premium.pricingPage.showOneTime') }}
            </button>
          </template>
          <template v-else>
            {{ t('homepage.pricingTeaser.yearlyNote', { price: yearlyPrice }) }}
            <button type="button" class="link" @click="billing = 'yearly'">
              {{ t('premium.pricingPage.showYearly') }}
            </button>
          </template>
        </p>

        <!-- Paid per job: bought in their own flows, so each card links there -->
        <div v-if="perJob.length" class="mt-20">
          <div class="mx-auto mb-10 max-w-3xl text-center">
            <h3 class="mb-4">{{ t('premium.pricingPage.perJob.title') }}</h3>
            <p class="text-lg">{{ t('premium.pricingPage.perJob.subtitle') }}</p>
          </div>

          <ul class="grid grid-cols-1 gap-6 md:grid-cols-2" :class="perJob.length > 4 ? 'xl:grid-cols-3' : 'xl:grid-cols-4'">
            <li
              v-for="item in perJob"
              :key="item.id"
              class="flex flex-col rounded-[0.625rem] bg-white p-7 shadow-lift dark:bg-gray-800"
            >
              <span class="mb-3 block font-title !text-lg font-semibold !text-ink dark:!text-white">
                {{ t(`premium.pricingPage.perJob.${item.key}.name`) }}
              </span>
              <div class="display-heading display-heading-4 mb-1">{{ item.price }}</div>
              <span class="mb-4 block !text-sm !text-muted dark:!text-gray-400">
                {{ t(`premium.pricingPage.perJob.${item.key}.period`) }}
              </span>
              <p class="mb-6 !text-base">{{ t(`premium.pricingPage.perJob.${item.key}.text`) }}</p>
              <div class="mt-auto">
                <SofaxBtn :to="item.to" :label="t(`premium.pricingPage.perJob.${item.key}.cta`)" variant="outline" block />
              </div>
            </li>
          </ul>

          <p class="mt-8 text-center text-base">{{ t('premium.pricingPage.perJob.credit') }}</p>
        </div>
      </div>
    </div>
  </section>
</template>
